import { IOnboarding } from '@/modules/partner/model';
import { PaymentInfoForm } from '@/modules/partner/model/form';
import { TPartnerType } from '@/modules/partner/model/partnerType';
import { put } from '@/shared/api/fetch-based-http-methods';
import { prepareDateForRequest } from '@/shared/api/prepare-date';
import { ISubmitFormResult } from '@/shared/models/formSubmit';
import { formatAddress } from '@/shared/utils/formatAddress';
import { getApiUrl } from '@/shared/utils/getApiUrl';
import { w4dOnboardingRemoteAPI } from '@src-common/api/definitions/w4d-onboarding-endpoints';
import { TOnboarding as TRawOnboarding } from '@src-common/api/definitions/w4d-onboarding-types';
import { createClient } from '@src-common/api/src/create-client';

import { IHelloForm } from '../model/helloStep';
import { PayoutsForm, mapPhoLicenseToApi } from '../model/payoutsStep';
import { splitStepErrors } from '../model/stepErrors';
import { WelcomeForm } from '../model/welcomeStep';

const client = createClient(w4dOnboardingRemoteAPI({ apiHost: getApiUrl(), put }));

const WELCOME_FIELDS: Record<string, keyof WelcomeForm & string> = {
  city_id: 'cityId',
  first_name: 'firstName',
  user_exists: 'firstName',
  last_name: 'lastName',
  middle_name: 'middleName',
  email: 'email',
  chauffeur_promo_code: 'referralCode',
};

const HELLO_FIELDS: Record<string, keyof IHelloForm & string> = {
  birthday: 'birthday',
  gender: 'gender',
  home_address: 'homeAddressLine1',
  license_number: 'licenseNumber',
  uk_national_insurance: 'ukNationalInsurance',
  personal_inn: 'personalInn',
};

const PAYOUTS_FIELDS: Record<string, keyof PayoutsForm & string> = {
  partner_type: 'partnerType',
  company_name: 'companyName',
  company_address: 'companyAddress',
  company_reg_number: 'companyRegNumber',
  vat_reg_no: 'vatRegNo',
  inn: 'inn',
  kpp: 'kpp',
  tax_profile: 'taxProfile',
  ssn: 'ssn',
  dba_name: 'dbaName',
  zip_code: 'zipCode',
  city: 'city',
  license_number: 'licenseNumber',
  ein: 'ein',
  has_pho_license: 'hasPhoLicense',
};

const PAYMENT_INFO_FIELDS: Record<string, keyof PaymentInfoForm & string> = {
  account_number: 'accountNumber',
  sort_code: 'sortCode',
  routing_number: 'routingNumber',
  bik: 'bik',
  swift: 'swift',
  iban: 'iban',
  ras_account: 'rasAccount',
  kor_account: 'corAccount',
  bank_name: 'bankName',
};

const preparePartnerOnboarding = (onboarding?: TRawOnboarding | null): IOnboarding => ({
  isCompleted: onboarding?.completed || false,
  lastStepName: onboarding?.last_step_name || 'welcome',
  isLastStepCompleted: onboarding?.last_step_completed || false,
});

interface IOnboardingStepSubmitResult<
  TForm extends Record<string, any>,
> extends ISubmitFormResult<TForm> {
  onboarding: IOnboarding;
}

interface WelcomeFormSubmitResult extends IOnboardingStepSubmitResult<WelcomeForm> {
  driverId: string;
}

const onboardingAPI = {
  submitWelcomeStep: async (formValues: WelcomeForm): Promise<WelcomeFormSubmitResult> => {
    const response = await client.updateWelcomeStep({
      payload: {
        city_id: formValues.cityId,
        first_name: formValues.firstName,
        last_name: formValues.lastName,
        middle_name: formValues.middleName,
        email: formValues.email,
        chauffeur_promo_code: formValues.referralCode,
        source: formValues.source,
      },
    });

    return {
      ...splitStepErrors(response.errors, WELCOME_FIELDS),
      driverId: response.driver?.id ?? '',
      onboarding: preparePartnerOnboarding(response.partner?.onboarding),
    };
  },

  submitHelloStep: async (
    formValues: IHelloForm,
  ): Promise<IOnboardingStepSubmitResult<IHelloForm>> => {
    const response = await client.updateHelloStep({
      payload: {
        birthday: prepareDateForRequest(formValues.birthday),
        // Omitted rather than null: the server keeps the stored value when the key is absent
        gender: formValues.gender ?? undefined,
        home_address: formatAddress(formValues),
        license_number: formValues.licenseNumber,
        uk_national_insurance: formValues.ukNationalInsurance,
        personal_inn: formValues.personalInn,
        license_authority: formValues.licensingAuthority,
      },
    });

    return {
      ...splitStepErrors(response.errors, HELLO_FIELDS),
      onboarding: preparePartnerOnboarding(response.partner?.onboarding),
    };
  },

  submitPayoutsStep: async (
    formValues: PayoutsForm,
  ): Promise<IOnboardingStepSubmitResult<PayoutsForm>> => {
    const response = await client.updatePayoutsStep({
      payload: {
        partner_type: formValues.partnerType,
        company_name: formValues.companyName,
        company_address: formValues.companyAddress,
        company_reg_number: formValues.companyRegNumber,
        vat_reg_no: formValues.vatRegNo,
        inn: formValues.inn,
        kpp: formValues.kpp,
        tax_profile: formValues.taxProfile,
        ssn: formValues.ssn,
        dba_name: formValues.dbaName,
        zip_code: formValues.zipCode,
        city: formValues.city,
        license_number: formValues.licenseNumber,
        ein: formValues.ein,
        has_pho_license: mapPhoLicenseToApi(formValues.hasPhoLicense),
      },
    });

    return {
      ...splitStepErrors(response.errors, PAYOUTS_FIELDS),
      onboarding: preparePartnerOnboarding(response.partner?.onboarding),
    };
  },

  submitPaymentInfoStep: async (
    formValues: PaymentInfoForm,
  ): Promise<IOnboardingStepSubmitResult<PaymentInfoForm>> => {
    const response = await client.updatePaymentInfoStep({
      payload: {
        account_number: formValues.accountNumber,
        sort_code: formValues.sortCode,
        routing_number: formValues.routingNumber,
        bik: formValues.bik,
        swift: formValues.swift,
        iban: formValues.iban,
        ras_account: formValues.rasAccount,
        kor_account: formValues.corAccount,
        bank_name: formValues.bankName,
      },
    });

    return {
      ...splitStepErrors(response.errors, PAYMENT_INFO_FIELDS),
      onboarding: preparePartnerOnboarding(response.partner?.onboarding),
    };
  },

  completeOnboarding: async (): Promise<IOnboarding> => {
    const response = await client.completeOnboarding();

    if (!response.partner.onboarding) {
      throw new Error('Partner onboarding field is missing');
    }

    return preparePartnerOnboarding(response.partner.onboarding);
  },

  updatePartnerType: async (type: TPartnerType) =>
    client.updatePartnerType({ payload: { partner_type: type } }),
};

export { onboardingAPI };
export type { ISubmitFormResult };
